import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, Loader2, CheckCircle2, UserRound, Store, Camera } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { openExternal } from "../lib/voice";
import { RechargeScannerCard } from "./RechargeScannerCard";

/* WhatsApp recharge: amount → one tap opens the agent's / owner's WhatsApp with the request filled in; a pending request
   is logged so Approve (agent or admin) credits the wallet after the money arrives. No UPI links — nothing for NPCI to decline. */
export const WhatsAppPayPanel = ({ amount, tx, onRequested }) => {
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { api.get("/recharge/whatsapp-target").then((r) => setTarget(r.data)).catch(() => setTarget({ configured: false })); }, []);
  const startRef = useRef(null);
  useEffect(() => { const go = () => startRef.current?.(); window.addEventListener("rl-autopay-go", go); return () => window.removeEventListener("rl-autopay-go", go); }, []);
  const start = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error(tx("Enter the amount first"));
    setBusy(true);
    try {
      const { data } = await api.post("/recharge/whatsapp-request", { amount: amt });
      setSent(data);
      onRequested?.(data);
      openExternal(data.wa_url);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open WhatsApp"); }
    setBusy(false);
  };
  startRef.current = () => { if (!busy) start(); };
  const uploadReceipt = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/upload/screenshot", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.post(`/recharge/whatsapp-request/${sent.id}/receipt`, { screenshot_path: up.data.path, screenshot_hash: up.data.hash });
      if (data.status === "success") { toast.success(tx("Payment received — wallet credited!")); setSent({ ...sent, status: "success", amount: data.amount }); onRequested?.(data); }
      else { toast.info(tx("Receipt sent — waiting for OK")); setSent({ ...sent, receipt: "pending" }); onRequested?.(data); }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read the receipt — try a clearer screenshot"); }
    setUploading(false);
  };
  const isAgent = target?.kind === "agent";
  if (target && !target.configured) return (
    <div className="rl-card p-3 border-2 border-amber-200 text-xs text-amber-900" data-testid="wa-pay-unconfigured">{tx("WhatsApp recharge is not set up yet — please contact support.")}</div>
  );
  return (
    <div className="rl-card p-3 border-2 border-emerald-200 space-y-2.5" data-testid="wa-pay-panel">
      <div className="flex items-center gap-2 text-emerald-800 font-display font-black text-sm"><MessageCircle className="w-4 h-4 text-emerald-600" />{tx("Pay via WhatsApp")}</div>
      {target && (
        <div className="flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-100 px-2.5 py-2" data-testid="wa-pay-target">
          <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">{isAgent ? <Store className="w-4 h-4" /> : <UserRound className="w-4 h-4" />}</div>
          <div className="text-xs min-w-0">
            <div className="font-bold text-slate-900 truncate">{target.name} <span className="text-[10px] font-semibold text-emerald-700">· {isAgent ? tx("your agent") : tx("ROYAL LAKSHMI office")}</span></div>
            <div className="font-mono text-[11px] text-slate-600">+91 {target.number}</div>
          </div>
        </div>
      )}
      <ol className="text-[11px] text-slate-700 space-y-1 pl-1">
        <li>1️⃣ {tx("Pick the amount above and tap the green button — WhatsApp opens with your request typed in.")}</li>
        <li>2️⃣ {tx("They send you the QR / UPI → you pay there.")}</li>
        <li>3️⃣ {tx("Once the money arrives they tap Approve → your wallet is credited and you get a notification.")}</li>
      </ol>
      <button onClick={start} disabled={busy || !target} className="w-full rounded-2xl p-3 text-left text-white flex items-center gap-3 active:scale-[0.98] transition-transform disabled:opacity-60" style={{ background: "linear-gradient(120deg,#128C7E,#25D366)" }} data-testid="wa-pay-start">
        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <MessageCircle className="w-5 h-5" />}</div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-sm leading-tight">{tx("Pay")} {amount ? `₹${amount}` : ""} {tx("on WhatsApp")}</div>
          <div className="text-[10px] text-white/85">{tx("Opens WhatsApp chat with")} {target?.name || "…"}</div>
        </div>
        <span className="bg-white text-emerald-700 text-[10px] font-black px-3 py-1.5 rounded-full shrink-0">WhatsApp</span>
      </button>
      {sent && sent.status !== "success" && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-2.5 text-xs text-emerald-900" data-testid="wa-pay-sent">
          <div className="flex gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
            <div>
              <b>{tx("Request noted")} · ₹{sent.amount} · Ref {sent.code}</b>
              <div className="text-[11px] mt-0.5">{tx("Pay to the scanner below (or in WhatsApp). Your wallet credits as soon as they approve — check Records.")}</div>
              <a href={sent.wa_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-1 text-[11px] font-bold underline" data-testid="wa-pay-reopen">{tx("WhatsApp did not open? Tap here")}</a>
            </div>
          </div>
          <div className="mt-2"><RechargeScannerCard rid={sent.id} tx={tx} onPaid={() => onRequested?.(sent)} /></div>
          <label className={`mt-2 w-full rounded-xl py-2.5 flex items-center justify-center gap-2 text-xs font-black cursor-pointer active:scale-[0.98] transition-transform ${uploading ? "opacity-60" : ""} bg-slate-900 text-amber-300`} data-testid="wa-pay-receipt-label">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}{uploading ? tx("Checking receipt…") : tx("Paid? Upload the receipt → instant credit")}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadReceipt(e.target.files[0])} disabled={uploading} data-testid="wa-pay-receipt-input" />
          </label>
          {sent.receipt === "pending" && <div className="mt-1.5 text-[11px] font-semibold text-amber-900 bg-amber-100 rounded-lg px-2 py-1" data-testid="wa-pay-receipt-pending">🧾 {tx("Receipt sent — they will tap OK in a moment and your wallet will be credited.")}</div>}
          <div className="mt-1.5 text-[10px] text-slate-600 rounded-lg bg-white/70 border border-emerald-100 px-2 py-1" data-testid="wa-pay-share-hint">📲 <b>{tx("Faster")}:</b> {tx("after paying, tap Share on the GPay/PhonePe receipt → choose Royal Lakshmi — no screenshot, no upload.")}</div>
        </div>
      )}
      {sent?.status === "success" && (
        <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-3 flex items-center gap-2" data-testid="wa-pay-success">
          <CheckCircle2 className="w-7 h-7 text-emerald-600 shrink-0" />
          <div><div className="font-black text-emerald-800">{tx("Payment received!")}</div><div className="text-xs text-emerald-700">₹{sent.amount} {tx("credited to your wallet automatically.")}</div></div>
        </div>
      )}
    </div>
  );
};
